import React, { useState, useEffect } from 'react';
import { Link2, DollarSign, TrendingUp, Copy, Check, Plus, BarChart3 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SelfReferralManager = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [myContent, setMyContent] = useState([]);
  const [selfReferrals, setSelfReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState(null);
  const [commissionRate, setCommissionRate] = useState(30);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contentResponse, referralsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/user/content?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/referrals/content/my-self-referrals`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setMyContent(contentResponse.data);
      setSelfReferrals(referralsResponse.data.self_referrals || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load data',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReferralLink = async () => {
    if (!selectedContent) {
      toast({
        variant: 'destructive',
        title: 'No Content Selected',
        description: 'Please select content to create a referral link',
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/referrals/content/${selectedContent.id}/generate-self-referral-link?commission_rate=${commissionRate}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: 'Link Created!',
        description: `Your referral link with ${commissionRate}% commission is ready`,
      });

      setShowCreateModal(false);
      fetchData(); // Refresh the list
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create referral link',
      });
    }
  };

  const handleCopy = (link, token) => {
    navigator.clipboard.writeText(link);
    setCopied(token);
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard',
    });
    setTimeout(() => setCopied(''), 2000);
  };

  const totalEarnings = selfReferrals.reduce((sum, ref) => sum + (ref.earnings || 0), 0);
  const totalSales = selfReferrals.reduce((sum, ref) => sum + (ref.sales || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-400/10 to-purple-500/10 rounded-lg p-6 border border-yellow-500/30">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0">
            <DollarSign className="w-6 h-6 text-black" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-2">
              Sell Your Own Content & Set Your Commission
            </h3>
            <p className="text-gray-300">
              Create referral links for your content and choose your own commission rate. Share your links anywhere and earn money from every sale!
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <Link2 className="w-5 h-5 text-blue-400" />
              <span className="text-sm text-gray-400">Active Links</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {selfReferrals.length}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <span className="text-sm text-gray-400">Total Sales</span>
            </div>
            <div className="text-3xl font-bold text-white">
              {totalSales}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-yellow-400" />
              <span className="text-sm text-gray-400">Total Earnings</span>
            </div>
            <div className="text-3xl font-bold text-yellow-400">
              ${totalEarnings.toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create New Link */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Create New Referral Link</h4>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white mb-2">Select Your Content</Label>
              <select
                value={selectedContent?.id || ''}
                onChange={(e) => {
                  const content = myContent.find(c => c.id === e.target.value);
                  setSelectedContent(content);
                }}
                className="w-full bg-gray-900 border-gray-700 text-white rounded-md p-2"
              >
                <option value="">Select content...</option>
                {myContent.map((content) => (
                  <option key={content.id} value={content.id}>
                    {content.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-white mb-2">
                Your Commission Rate: {commissionRate}%
              </Label>
              <Slider
                value={[commissionRate]}
                onValueChange={(value) => setCommissionRate(value[0])}
                min={0}
                max={100}
                step={5}
                className="mt-2"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <p className="text-sm text-blue-300">
                💡 <strong>Set your own rate!</strong> Higher commission = More earnings per sale. Lower commission = More competitive pricing for buyers.
              </p>
            </div>

            <Button
              onClick={handleCreateReferralLink}
              disabled={!selectedContent}
              className="w-full bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Generate Referral Link
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing Referral Links */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-4">Your Referral Links</h4>
        
        {selfReferrals.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-8 text-center">
              <Link2 className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">No referral links yet</p>
              <p className="text-sm text-gray-500 mt-1">Create your first referral link above to start earning</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {selfReferrals.map((referral) => (
              <Card key={referral.id} className="bg-gray-800/50 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h5 className="text-white font-semibold mb-1">
                        {referral.content?.title || 'Content'}
                      </h5>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <BarChart3 className="w-4 h-4" />
                          {referral.sales || 0} sales
                        </span>
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ${(referral.earnings || 0).toFixed(2)} earned
                        </span>
                        <span className="text-yellow-400 font-semibold">
                          {referral.custom_commission_rate}% commission
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          value={referral.referral_link}
                          readOnly
                          className="bg-gray-900 border-gray-700 text-white text-sm flex-1"
                        />
                        <Button
                          onClick={() => handleCopy(referral.referral_link, referral.referral_token)}
                          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black"
                        >
                          {copied === referral.referral_token ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Copied
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfReferralManager;
